import React, { useEffect, useState } from "react";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Tag, Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getAnnouncements,
  deleteAnnouncement,
} from "src/services/AdminPortalAPIs";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import {Announcement} from "src/layouts/AdminPortal/AnnouncementsPage/AnnouncementInterface";
import StyledInput from "src/components/reusable/StyledInput";

const statusColors: Record<string, string> = {
  published: "success",
  scheduled: "warning",
  draft: "default",
};

const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAnnouncement,setDeletingAnnouncement,] = useState<Announcement | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Announcement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const didFetchRef = React.useRef(false);

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;

    getAnnouncements()
      .then(res => setAnnouncements(res.data.data || []))
      .catch(() => message.error("Failed to fetch announcements"))
      .finally(() => setLoading(false));
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingAnnouncement) {return;}

    try {
      await deleteAnnouncement({
        announcement_id: deletingAnnouncement.announcement_id,
      });
      message.success("Announcement deleted successfully.");
      setAnnouncements(prev =>
        prev.filter(
          a => a.announcement_id !== deletingAnnouncement.announcement_id
        )
      );
    } catch {
      message.error("Failed to delete announcement.");
    } finally {
      setDeleteModalOpen(false);
      setDeletingAnnouncement(null);
    }
  };

const filteredAnnouncements = React.useMemo(() =>
  announcements.filter(
    item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.target_audience.toLowerCase().includes(searchTerm.toLowerCase())
  ),
  [announcements, searchTerm]
);

  return (
    <div className="admin-portal-page">
      <div className="header">
        <span className="heading-primary">Announcements</span>
        <Button
        className="button button--connection"
          type="primary"
          onClick={() => navigate("/admin-portal/create-announcement")}
        >
          + New Announcement
        </Button>
      </div>

      <div className="search-bar">
        <StyledInput
  placeholder="Search announcements..."
  value={searchTerm}
  onChange={e => setSearchTerm(e.target.value)}
  className="announcement-search"
/>
      </div>
{loading ? (
  <div style={{ textAlign: "center", marginTop: 40 }}>
    <span>Loading...</span>
  </div>
) : (
      <div className="announcement-list">
        {filteredAnnouncements.map((item, index) => (
          <div className="announcement-card" key={item.announcement_id}>
            <div className="announcement-header">
              <div>
                <h2>{item.title}</h2>
                <span className="meta">
                  {item.published_at || "Not published"}
                </span>
              </div>
              <Tag color={statusColors[item.status.toLowerCase()] || "default"}>
  {item.status}
</Tag>
            </div>
            <p className="body-text truncate">{item.content}</p>
            {item.url && (
              <div style={{ marginBottom: "0.75rem" }}>
                <strong>Navigate to: </strong>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-link"
                  style={{
                    color: "#2a73c2",
                    textDecoration: "underline",
                    wordBreak: "break-word",
                    marginLeft: 4,
                  }}
                >
                  {item.url}
                </a>
              </div>
            )}

            <div className="tags">
              <Tag>{item.target_audience}</Tag>
            </div>

            <div className="actions">
              {item.status.toLowerCase() !== "published" && (
                <Button
                  icon={<EditOutlined />}
                  type="link"
                  aria-label="Edit Announcement"
                  onClick={() =>
                    navigate("/admin-portal/edit-announcement", {
                      state: { announcement: item },
                    })
                  }
                >
                  Edit
                </Button>
              )}
              <Button
                icon={<DeleteOutlined />}
                type="link"
                danger
                aria-label="Delete Announcement"
                onClick={() => {
                  setDeletingAnnouncement(item);
                  setDeleteModalOpen(true);
                }}
              >
                Delete
              </Button>
              <Button
                icon={<EyeOutlined />}
                type="link"
                aria-label="View Announcement Details"
                onClick={() => {
                  setSelected(item);
                  setIsModalOpen(true);
                }}
              >
                View Details
              </Button>
            </div>
          </div>
        ))}
      </div>
)}
      {/* View Details Modal */}
      <Modal
        title={selected?.title}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        {selected && (
          <>
            <p className="body-text">{selected.content}</p>
            <div className="tags" style={{ marginBottom: "8px" }}>
              <Tag>{selected.target_audience}</Tag>
            </div>
            <div style={{ fontSize: "13px", color: "#444" }}>
              <p>
                <strong>Created:</strong> {selected.created_at}
              </p>
              <p>
                <strong>Scheduled For:</strong>{" "}
                {selected.scheduled_for || "N/A"}
              </p>
              <p>
                <strong>Published At:</strong> {selected.published_at || "N/A"}
              </p>
              {selected?.url && (
                <div style={{ marginTop: 16 }}>
                  <strong>Action Button:</strong>{" "}
                  <Button
                  className="button button--primary"
                    type="primary"
                    href={selected.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: 12 }}
                    aria-label="Visit Announcement Link"
                  >
                    Visit Link
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
      <ReusableModal
        modalOpen={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeletingAnnouncement(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Announcement?"
        description="Are you sure you want to delete this announcement? This action cannot be undone."
        icon="/Icons/announcements.svg"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AnnouncementsPage;
